// (Copyright) Confluent, Inc.

import * as React from 'react';
import { css } from 'styled-components';
import lottie from 'lottie-web/build/player/lottie_light.min';

import submittingAnimationPrimary from '../../../static/animations/button-submitting-primary.json';
import submittingAnimationSecondary from '../../../static/animations/button-submitting-secondary.json';
import type { ButtonClassType, ButtonSize } from '../Button';
import { usePrevious } from '../../../hooks/usePrevious';
import { useCTranslation } from '../../../utils/Translators';

export type FinishBehavior = 'SHOW_CHECKMARK' | 'SHOW_AND_HOLD_CHECKMARK' | 'NONE';

export const finishBehaviors: Array<FinishBehavior> = [
  'SHOW_CHECKMARK',
  'SHOW_AND_HOLD_CHECKMARK',
  'NONE',
];

type Props = {
  buttonClassType: ButtonClassType;
  buttonSize: ButtonSize;
  finishBehavior: FinishBehavior | null | undefined;
  onAnimationFinished: () => void | null | undefined;
  submitting: boolean;
};

type ButtonSubmittingIndicator = {
  indicator: React.ReactNode;
  isActive: boolean;
};

// The spinner animation is made up of three segments: the initial (ease in) segment, the looping
// segment, and the final checkmark segment. The final checkmark segment is only presented if
// the finish behaviro is eitehr SHOW_CHECKMARK or SHOW_AND_HOLD_CHECKMARK.
type AnimationSegment = 'INITIAL' | 'SPINNER' | 'CHECKMARK' | 'INACTIVE';

type AnimationState = {
  requestedFinishBehavior: FinishBehavior;
  segment: AnimationSegment;
};

type AnimationAction =
  | {
      type: 'SUBMITTING_STARTED';
    }
  | {
      type: 'SUBMITTING_STOPPED';
    }
  | {
      type: 'LOOP_COMPLETED';
    }
  | {
      behavior: FinishBehavior;
      type: 'SET_FINISH_BEHAVIOR';
    };

const defaultFinishBehavior: FinishBehavior = 'NONE';

// This symbol is added to the lottie animation instance when it has been destroyed.
//
// You can test if the lottie object has been destroyed by checking if `animation[destroyedSymbol]`
// is true. This is required to workaround certain API limitations in lottie-web (exceptions thrown
// when calling `removeEventListener` on destroyed animation instances.
const destroyedSymbol = Symbol('destroyed');

/**
 * Return the frame indices for a given segment, which is passed to lottie-web's playSegments API.
 * See https://github.com/airbnb/lottie-web#playsegmentssegments-forceflag
 *
 * These frame index values were provided by design.
 *
 * @param {AnimationSegment} segment The segment to play.
 * @returns {Array} A tuple-like array of the start and end frame indexes for a given segment, or
 * an array of tuples for multiple sequences.
 */
const getSegmentFrames = (
  segment: AnimationSegment
): Array<[number, number]> | number | null | undefined => {
  switch (segment) {
    case 'INITIAL':
      // Include the LOOP frames in this sequence so that the animation proceeds smoothly without
      // any frame drops

      return [
        [0, 44],
        [45, 64],
      ];
    case 'SPINNER':
      return [[45, 64]];
    case 'CHECKMARK':
      return [[65, 108]];
    default:
      return null;
  }
};

/**
 * Returns the width/height dimension of the indicator div for a given button size.
 *
 * @param {ButtonSize} size The button size enum value.
 * @returns {Number} The indicator width/height.
 */
const getSize = (size: ButtonSize): number => {
  switch (size) {
    case 'LARGE':
      return 32;
    default:
      return 24;
  }
};

const animationReducer = (state: AnimationState, action: AnimationAction) => {
  switch (action.type) {
    case 'SUBMITTING_STARTED':
      return {
        ...state,
        segment: 'INITIAL',
      };
    case 'SUBMITTING_STOPPED':
      if (state.segment !== 'INACTIVE') {
        // Ignore "FINISH" events if we're not currently animating

        switch (state.requestedFinishBehavior) {
          case 'SHOW_CHECKMARK':
          case 'SHOW_AND_HOLD_CHECKMARK':
            return {
              ...state,
              segment: 'CHECKMARK',
            };
          default:
            return {
              ...state,
              segment: 'INACTIVE',
            };
        }
      }

      return state;
    case 'LOOP_COMPLETED':
      switch (state.segment) {
        case 'INITIAL':
          return {
            ...state,
            segment: 'SPINNER',
          };
        case 'CHECKMARK':
          if (state.requestedFinishBehavior !== 'SHOW_AND_HOLD_CHECKMARK') {
            return {
              ...state,
              segment: 'INACTIVE',
            };
          }

          return state;
        default:
          return state;
      }
    case 'SET_FINISH_BEHAVIOR':
      return {
        ...state,
        requestedFinishBehavior: action.behavior,
      };
    default:
      return state;
  }
};

const useLoadAnimation = ({ containerRef, animationRef, isActive, buttonClassType, dispatch }) => {
  React.useEffect(() => {
    if (isActive) {
      const animation = lottie.loadAnimation({
        container: containerRef.current,
        renderer: 'svg',
        loop: true,
        autoplay: false,
        animationData:
          buttonClassType === 'PRIMARY' ? submittingAnimationPrimary : submittingAnimationSecondary,
      });

      animation.addEventListener('loopComplete', () => {
        dispatch({ type: 'LOOP_COMPLETED' });
      });

      animationRef.current = animation;
    }

    return () => {
      if (animationRef.current) {
        const animation = animationRef.current;

        animation[destroyedSymbol] = true;
        animation.destroy();
        animationRef.current = null;
      }
    };
  }, [animationRef, buttonClassType, containerRef, dispatch, isActive]);
};

const useSetFinishBehaviorAction = ({ dispatch, finishBehavior }) => {
  React.useEffect(() => {
    dispatch({
      type: 'SET_FINISH_BEHAVIOR',
      behavior: finishBehavior,
    });
  }, [dispatch, finishBehavior]);
};

const useSubmittingActions = ({ dispatch, submitting }) => {
  React.useEffect(() => {
    if (submitting) {
      dispatch({ type: 'SUBMITTING_STARTED' });
    } else {
      dispatch({ type: 'SUBMITTING_STOPPED' });
    }
  }, [dispatch, submitting]);
};

const useCheckAnimationFinished = ({ segment, animationRef, onAnimationFinished }) => {
  const prevSegment = usePrevious(segment);

  React.useEffect(() => {
    // Handle checking if animation finished in the case the animation was held on the last frame
    // (i.e. finish behavior is "SHOW_AND_HOLD_CHECKMARK"), as we will never be transitioning to
    // the INACTIVE segment.

    const animation = animationRef.current;

    if (animation) {
      if (onAnimationFinished) {
        if (__TEST__) {
          // Lottie will not call complete event listeners in a testing environment, so immediately
          // call the callback instead

          onAnimationFinished();

          return;
        }

        animation.addEventListener('complete', onAnimationFinished);

        return () => {
          if (!animation[destroyedSymbol]) {
            // Check animationRef.current exists first as we do not want to remove the event
            // listener if the animation has been destroyed (on unmount)
            animation.removeEventListener('complete', onAnimationFinished);
          }
        };
      }
    }
  }, [prevSegment, segment, onAnimationFinished, animationRef]);

  React.useEffect(() => {
    // Handle checking if animation finished when we transition back to the INACTIVE segment.

    if (prevSegment && prevSegment !== 'INACTIVE' && segment === 'INACTIVE') {
      if (onAnimationFinished) {
        onAnimationFinished();
      }
    }
  }, [prevSegment, segment, onAnimationFinished]);
};

const useControlAnimationLoop = ({ segment, animationRef, finishBehavior }) => {
  React.useEffect(() => {
    const animation = animationRef.current;

    if (animation) {
      // We should loop the animation in all scenarios, except when it's the checkmark animation
      // and the finishing behavior is "SHOW_AND_HOLD_CHECKMARK".
      const shouldLoop = segment !== 'CHECKMARK' || finishBehavior !== 'SHOW_AND_HOLD_CHECKMARK';
      animation.loop = shouldLoop;
    }
  }, [segment, finishBehavior, animationRef]);
};

const usePlayAnimationSegments = ({ animationRef, segment }) => {
  React.useEffect(() => {
    const segmentFrames = getSegmentFrames(segment);
    const animation = animationRef.current;

    if (animation) {
      if (segmentFrames) {
        // Only "force" the segment when starting the initial segment. If `force` is false, the
        // current segment will play out before transitioning to the new segments.
        const force = segment === 'INITIAL';

        animation.playSegments(segmentFrames, force);
      }
    }
  }, [segment, animationRef]);
};

export const useButtonSubmittingIndicator = (props: Props): ButtonSubmittingIndicator => {
  const { buttonClassType, submitting, buttonSize, onAnimationFinished } = props;
  const finishBehavior = props.finishBehavior ?? defaultFinishBehavior;
  const containerRef = React.useRef();
  const animationRef = React.useRef();
  const size = getSize(buttonSize);
  const [{ segment }, dispatch] = React.useReducer(animationReducer, {
    segment: 'INACTIVE',
    requestedFinishBehavior: finishBehavior,
  });
  const isActive = segment !== 'INACTIVE';
  const [t] = useCTranslation();

  useLoadAnimation({ isActive, buttonClassType, containerRef, animationRef, dispatch });
  useSetFinishBehaviorAction({ finishBehavior, dispatch });
  useSubmittingActions({ submitting, dispatch });
  useCheckAnimationFinished({ onAnimationFinished, segment, animationRef });
  useControlAnimationLoop({ segment, animationRef, finishBehavior });
  usePlayAnimationSegments({ segment, animationRef });

  return {
    isActive,
    indicator: (
      <div
        aria-label={isActive ? t('Shared.Button.Submitting') : ''}
        css={css`
          position: absolute;
          top: 50%;
          left: 50%;
          width: ${size}px;
          height: ${size}px;
          margin-left: -${size / 2}px;
          margin-top: -${size / 2}px;
          opacity: ${isActive ? 1 : 0};
        `}
        data-animation-segment={segment}
        data-testid="submitting-indicator"
        ref={containerRef}
      />
    ),
  };
};
